import { createClient } from 'next-sanity'
import { cache } from 'react'
import type { SanityImageSource } from '@sanity/image-url/lib/types/types'
import { urlFor } from '../sanity/lib/image'

const projectId = process.env.NEXT_PUBLIC_SANITY_PROJECT_ID!
const dataset = process.env.NEXT_PUBLIC_SANITY_DATASET!
const apiVersion = process.env.NEXT_PUBLIC_SANITY_API_VERSION || '2022-06-30'

// Create the client (CDN in prod speeds up reads; can be slightly behind published updates)
export const client = createClient({
  projectId,
  dataset,
  apiVersion,
  useCdn: process.env.NODE_ENV === 'production',
})

// Live API — use for homepage featured list so builds/ISR match Studio (CDN can cache empty/stale GROQ results)
const serverReadClient = createClient({
  projectId,
  dataset,
  apiVersion,
  useCdn: false,
})

// Types based on our Sanity schemas
export interface SanityImage {
  _type: 'image'
  /** Reference shape from bare GROQ, or expanded `asset->{url}` from dereferenced queries */
  asset?: {
    _ref?: string
    _type?: string
    url?: string
  }
  alt?: string
}

export interface SanityVideo {
  _type: 'file'
  asset?: {
    _ref?: string
    _type?: string
    url?: string
  }
}

export interface Category {
  _id: string
  title: string
  slug: {
    current: string
  }
  description?: string
  color?: string
}

export interface Client {
  _id: string
  name: string
  slug: {
    current: string
  }
  description?: string
  logo?: SanityImage
  website?: string
}

export interface Project {
  _id: string
  title: string
  id: string
  description?: string
  client?: Client
  category?: Category
  media?: Array<{
    _key: string
    _type: 'mediaItem'
    type: 'image' | 'video'
    image?: SanityImage
    video?: SanityVideo
    coverImage?: SanityImage
    alt?: string
    caption?: string
  }>
  featured?: boolean
  year?: number
  sector?: string
  tags?: string[]
  sortOrder?: number
}

export interface ModelProject {
  _id: string
  title: string
  id: string
  description?: string
  client?: Client
  year?: number
  modelGallery: SanityImage[]
  tags?: string[]
  sortOrder?: number
}

export interface ProductProject {
  _id: string
  title: string
  id: string
  client?: Client
  year?: number
  longDescription: string
  productGallery: SanityImage[]
  tags?: string[]
  sortOrder?: number
}

export interface SocialLink {
  platform: string
  url: string
  displayName: string
}

/** True when GROQ returned either a stored reference or a dereferenced asset with CDN url */
export const sanityAssetResolvable = (
  asset: { _ref?: string; url?: string } | undefined | null,
): boolean => !!(asset && (asset._ref || asset.url))

export const mediaKindIs = (value: unknown, kind: 'video' | 'image'): boolean =>
  String(value ?? '').toLowerCase().trim() === kind

export interface ProfileProject {
  _id: string
  title: string
  about: any[] // Portable Text blocks
  services: string[]
  contactEmail: string
  studioLocation: string
  socialLinks: SocialLink[]
  clients: string[]
  newsletterEnabled: boolean
  newsletterTitle: string
  headshot?: SanityImage
}

// GROQ queries
const PROJECT_QUERY = `*[_type == "project"] | order(sortOrder asc, _createdAt desc) {
  _id,
  title,
  id,
  description,
  client->{
    _id,
    name,
    slug,
    description,
    logo,
    website
  },
  category->{
    _id,
    title,
    slug,
    description,
    color
  },
  media[]{
    _key,
    _type,
    type,
    alt,
    caption,
    image {
      _type,
      alt,
      crop,
      hotspot,
      asset->{
        _id,
        _type,
        url
      }
    },
    coverImage {
      _type,
      alt,
      crop,
      hotspot,
      asset->{
        _id,
        _type,
        url
      }
    },
    video {
      _type,
      asset->{
        _id,
        _type,
        url
      }
    }
  },
  featured,
  year,
  sector,
  tags,
  sortOrder
}`

const CATEGORIES_QUERY = `*[_type == "category"] | order(title asc) {
  _id,
  title,
  slug,
  description,
  color
}`

const CLIENTS_QUERY = `*[_type == "client"] | order(name asc) {
  _id,
  name,
  slug,
  description,
  logo,
  website
}`

const MODEL_PROJECTS_QUERY = `*[_type == "modelProject"] | order(sortOrder asc, _createdAt desc) {
  _id,
  title,
  id,
  description,
  client->{
    _id,
    name,
    slug,
    description,
    logo,
    website
  },
  year,
  modelGallery[]{
    _key,
    _type,
    alt,
    caption,
    asset->{
      _id,
      _type,
      url
    }
  },
  tags,
  sortOrder
}`

const PRODUCT_PROJECTS_QUERY = `*[_type == "productProject"] | order(sortOrder asc, _createdAt desc) {
  _id,
  title,
  id,
  client->{
    _id,
    name,
    slug,
    description,
    logo,
    website
  },
  year,
  longDescription,
  productGallery[]{
    _key,
    _type,
    alt,
    caption,
    crop,
    hotspot,
    asset->{
      _id,
      _type,
      url
    }
  },
  tags,
  sortOrder
}`

// Cached data fetching functions
export const getProjects = cache(async (): Promise<Project[]> => {
  try {
    // Same as featured: avoid Sanity API CDN caching stale/partial GROQ (covers/thumbnails missing on /work)
    const projects = await serverReadClient.fetch(PROJECT_QUERY)
    return projects || []
  } catch (error) {
    console.error('❌ Error fetching projects:', error)
    return []
  }
})

export const getCategories = cache(async (): Promise<Category[]> => {
  try {
    const categories = await client.fetch(CATEGORIES_QUERY)
    return categories || []
  } catch (error) {
    console.error('Error fetching categories:', error)
    return []
  }
})

export const getClients = cache(async (): Promise<Client[]> => {
  try {
    const clients = await client.fetch(CLIENTS_QUERY)
    return clients || []
  } catch (error) {
    console.error('Error fetching clients:', error)
    return []
  }
})

export const getFeaturedProjects = async (): Promise<Project[]> => {
  try {
    const featuredQuery = `*[_type == "project" && featured == true] | order(sortOrder asc, _createdAt desc) {
      _id,
      title,
      id,
      description,
      client->{
        _id,
        name,
        slug,
        description,
        logo,
        website
      },
      category->{
        _id,
        title,
        slug,
        description,
        color
      },
      media[]{
        _key,
        _type,
        type,
        alt,
        caption,
        image {
          _type,
          alt,
          crop,
          hotspot,
          asset->{
            _id,
            _type,
            url
          }
        },
        coverImage {
          _type,
          alt,
          crop,
          hotspot,
          asset->{
            _id,
            _type,
            url
          }
        },
        video {
          _type,
          asset->{
            _id,
            _type,
            url
          }
        }
      },
      featured,
      year,
      sector,
      tags,
      sortOrder
    }`
    const projects = await serverReadClient.fetch(featuredQuery)
    return projects || []
  } catch (error) {
    console.error('❌ Error fetching featured projects:', error)
    return []
  }
}

export const getProjectsByCategory = cache(async (categorySlug: string): Promise<Project[]> => {
  try {
    const categoryQuery = `*[_type == "project" && category->slug.current == $categorySlug] | order(sortOrder asc, _createdAt desc) {
      _id,
      title,
      id,
      description,
      client->{
        _id,
        name,
        slug,
        description,
        logo,
        website
      },
      category->{
        _id,
        title,
        slug,
        description,
        color
      },
      media[]{
        _key,
        _type,
        type,
        image{
          _key,
          _type,
          asset->{
            _id,
            url
          },
          alt,
          caption
        },
        video{
          _key,
          _type,
          asset->{
            _id,
            url
          },
          alt,
          caption
        },
        coverImage{
          _key,
          _type,
          asset->{
            _id,
            url
          },
          alt,
          caption
        }
      },
      featured,
      year,
      sector,
      tags,
      sortOrder
    }`
    const projects = await serverReadClient.fetch(categoryQuery, { categorySlug })
    return projects || []
  } catch (error) {
    console.error('❌ Error fetching projects by category:', error)
    return []
  }
})

export const getModelProjects = cache(async (): Promise<ModelProject[]> => {
  try {
    const projects = await serverReadClient.fetch(MODEL_PROJECTS_QUERY)
    return projects || []
  } catch (error) {
    console.error('❌ Error fetching model projects:', error)
    return []
  }
})

export const getProductProjects = cache(async (): Promise<ProductProject[]> => {
  try {
    const projects = await serverReadClient.fetch(PRODUCT_PROJECTS_QUERY)
    return projects || []
  } catch (error) {
    console.error('❌ Error fetching product projects:', error)
    return []
  }
})

// Profile Project query
const PROFILE_PROJECT_QUERY = `*[_type == "profileProject"][0] {
  _id,
  title,
  about,
  services,
  contactEmail,
  studioLocation,
  socialLinks,
  clients,
  newsletterEnabled,
  newsletterTitle,
  headshot {
    _type,
    alt,
    crop,
    hotspot,
    asset->{
      _id,
      _type,
      url
    }
  }
}`

export const getProfileProject = cache(async (): Promise<ProfileProject | null> => {
  try {
    const profileProject = await serverReadClient.fetch(PROFILE_PROJECT_QUERY)
    return profileProject || null
  } catch (error) {
    console.error('❌ Error fetching profile project:', error)
    return null
  }
})

// Helper function to get video URLs from Sanity
export const getVideoUrl = (video: SanityVideo | null | undefined): string => {
  if (!video?.asset) {
    return ''
  }

  if (video.asset.url) {
    return video.asset.url
  }

  if (!video.asset._ref) {
    return ''
  }

  try {
    const ref = video.asset._ref
    // Sanity file references are in format: file-{id}-{extension}
    const refParts = ref.split('-')
    
    if (refParts.length !== 3) {
      console.error('❌ Invalid video asset reference format:', ref)
      return ''
    }
    
    const [, id, extension] = refParts
    const videoUrl = `https://cdn.sanity.io/files/${projectId}/${dataset}/${id}.${extension}`
    
    return videoUrl
  } catch (error) {
    console.error('❌ Error constructing video URL:', error)
    return ''
  }
}

// Helper function to get image URLs from Sanity with transformations
export const getImageUrl = (
  image: SanityImage | null | undefined,
  width?: number,
  height?: number,
  quality: number = 80,
): string => {
  if (!image?.asset || !sanityAssetResolvable(image.asset)) {
    return ''
  }

  try {
    let chain = urlFor(image as SanityImageSource)
    if (width) chain = chain.width(width)
    if (height) chain = chain.height(height)
    return chain.quality(quality).fit('max').auto('format').url() || ''
  } catch (error) {
    console.error('❌ Error building image URL:', error)
    return ''
  }
} 